import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { 
  ArrowLeftIcon, 
  TrophyIcon, 
  UserIcon, 
  ChartBarIcon,
  ChevronRightIcon,
  HomeIcon 
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface HorseStandingsData {
  poolId: string
  poolName: string
  poolSettings: {
    track: string
    raceDate: string
  }
  totalParticipants: number
  totalRaces: number
  racesCompleted: number
  standings: HorseParticipant[]
}

interface HorseParticipant {
  userId: string
  name: string
  email: string
  joinedAt: string
  rank: number
  totalPoints: number
  winPicks: number
  placePicks: number
  showPicks: number
  totalPicks: number
  racesCompleted: number
  raceResults: RaceResult[]
}

interface RaceResult {
  raceNumber: number
  horseName: string
  postPosition: string
  postPositionColor?: {
    background_color: string
    text_color: string
    name: string
  }
  result: 'win' | 'place' | 'show' | 'loss' | 'pending'
  points: number
  isScratched: boolean
}

export default function HorseRacingPoolStandingsPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const { user, getAccessTokenSilently } = useAuth0()
  const [standingsData, setStandingsData] = useState<HorseStandingsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Removed selectedParticipant state - privacy protection

  useEffect(() => {
    if (poolId) {
      fetchStandings()
    }
  }, [poolId])

  const fetchStandings = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}/standings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch standings: ${response.statusText}`)
      }

      const data = await response.json()
      setStandingsData(data)
    } catch (err) {
      console.error('Error fetching standings:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch standings')
    } finally {
      setLoading(false)
    }
  }

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return '🥇'
      case 2:
        return '🥈'
      case 3:
        return '🥉'
      default:
        return `#${rank}`
    }
  }

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
      case 2:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20'
      case 3:
        return 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20'
      default:
        return 'text-gray-900 dark:text-white bg-white dark:bg-gray-800'
    }
  }

  const getResultColor = (result: string) => {
    switch (result) {
      case 'win':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'place':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'show':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'loss':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'pending':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const isCurrentUser = (userId: string) => {
    return user?.sub === userId
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
        </div>
      </div>
    )
  }

  if (!standingsData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No standings data available</h2>
          <p className="text-gray-600 dark:text-gray-300">Standings will appear once races begin and results are posted.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/pools"
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Pools
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <Link
                  to={`/pools/${poolId}`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white"
                >
                  {standingsData.poolName}
                </Link>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400">
                  Standings
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {standingsData.poolName} - Standings
              </h1>
              <p className="mt-1 text-lg text-gray-600 dark:text-gray-300">
                {standingsData.poolSettings?.track || 'Horse Racing'} • {standingsData.poolSettings?.raceDate || 'TBD'}
              </p>
            </div>
            <Link
              to={`/pools/${poolId}`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Pool
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <UserIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Participants</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{standingsData.totalParticipants}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <span className="text-2xl mr-4">🐎</span>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Races</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{standingsData.totalRaces}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <ChartBarIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Races Completed</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{standingsData.racesCompleted}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <TrophyIcon className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Progress</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.round((standingsData.racesCompleted / standingsData.totalRaces) * 100)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Standings Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Current Standings</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Scoring: Win (6 pts) • Place (3 pts) • Show (1 pt) • Cumulative scoring
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Participant
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Total Points
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Win/Place/Show
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Races Completed
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {standingsData.standings.map((participant) => (
                  <React.Fragment key={participant.userId}>
                    <tr
                      className={`${
                        isCurrentUser(participant.userId) 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRankColor(participant.rank)}`}>
                        {getRankIcon(participant.rank)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {participant.name}
                            {isCurrentUser(participant.userId) && (
                              <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(You)</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{participant.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">{participant.totalPoints}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900 dark:text-white">
                        <span className="text-green-600 dark:text-green-400 font-medium">{participant.winPicks}</span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span className="text-blue-600 dark:text-blue-400 font-medium">{participant.placePicks}</span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span className="text-yellow-600 dark:text-yellow-400 font-medium">{participant.showPicks}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {participant.racesCompleted} / {standingsData.totalRaces}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-gray-400 dark:text-gray-500 text-sm">
                        Private
                      </span>
                    </td>
                  </tr>
                  {/* Privacy: Removed expanded details - users should not see other participants' picks */}
                  {false && selectedParticipant === participant.userId && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 bg-gray-50 dark:bg-gray-700">
                        <div className="space-y-4">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            Race-by-Race Results for {participant.name}
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {participant.raceResults.map((result) => (
                              <div
                                key={result.raceNumber}
                                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    Race {result.raceNumber}
                                  </span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getResultColor(result.result)}`}>
                                    {result.result === 'pending' ? 'Pending' : 
                                     result.result === 'loss' ? 'Loss' :
                                     result.result.charAt(0).toUpperCase() + result.result.slice(1)}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {result.postPositionColor ? (
                                    <div
                                      className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-xs font-bold"
                                      style={{
                                        backgroundColor: result.postPositionColor.background_color,
                                        color: result.postPositionColor.text_color
                                      }}
                                    >
                                      {result.postPosition}
                                    </div>
                                  ) : (
                                    <div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-bold">
                                      {result.postPosition}
                                    </div>
                                  )}
                                  <div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {result.horseName}
                                      {result.isScratched && (
                                        <span className="ml-1 text-xs text-red-600 dark:text-red-400">
                                          (Scratched)
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {result.points} points
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}
